import Async from "components/DesignSystem/AsyncComponents/Async";
import { Avatar } from "components/DesignSystem/AvatarGroup/Avatar";
import { Button } from "components/DesignSystem/Button/Button";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { Header } from "components/DesignSystem/Header/Header";
import { PaginationHandler } from "components/DesignSystem/Pagination/Pagination";
import { Search } from "components/DesignSystem/Search/Search";
import { DashboardLayout } from "components/DashboardLayout";
import { TableUI } from "components/design/TableUI";
import { EmptyScreen } from "pages/Books/EmptyScreen";
import { usePageTitle } from "hooks/usePageTitle";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { useModal } from "hooks/useModal";
import { useDebounce } from "utils/debounce";
import dayjs from "dayjs";
import { DISPLAY_DATE } from "constants/date";
import ThreeDots from "static/images/ThreeDots.svg";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import {
  IncorporationBaseTemplate,
  useDeleteFormTemplateMutation,
  useGetFormTemplateListQuery,
} from "store/apis/incorporationFormBuilder";
import { useToast } from "hooks/useToast";
import { BackendError } from "types/utils/error";
import { useState, MouseEvent } from "react";
import ToolTip from "components/design/toolTip";
import { DeleteTemplateModal } from "./DeleteTemplateModal";
import { CreateTemplateModal } from "./CreateTemplateModal";
import { MobileIncorporationTemplateRow } from "./MobileIncorporationTemplateRow";
import { useWindowSize } from "hooks/useWindowSize";
import { useHistory } from "react-router-dom";

const PAGE_SIZE = 20;

const IncorporationTemplateActions = ({
  template,
  onEdit,
  onDelete,
}: {
  template: IncorporationBaseTemplate;
  onEdit: (template: IncorporationBaseTemplate) => void;
  onDelete: (template: IncorporationBaseTemplate) => void;
}) => {
  const handleClick = (e: MouseEvent) => {
    e.stopPropagation();
  };

  const handleEditSelect = () => onEdit(template);
  const handleDeleteSelect = () => onDelete(template);

  return (
    <div onClick={handleClick}>
      <Dropdown.Root>
        <Dropdown.Trigger asChild>
          <img src={ThreeDots} alt="Show more options" />
        </Dropdown.Trigger>
        <Dropdown.Portal>
          <Dropdown.Content sideOffset={6} align="end">
            <Dropdown.Item onSelect={handleEditSelect}>
              Edit template
            </Dropdown.Item>
            <Dropdown.Item type="danger" onSelect={handleDeleteSelect}>
              Delete template
            </Dropdown.Item>
          </Dropdown.Content>
        </Dropdown.Portal>
      </Dropdown.Root>
    </div>
  );
};

export const IncorporationTemplates = () => {
  usePageTitle("Incorporation Templates");
  const history = useHistory();
  const { isMobile } = useWindowSize();
  const query = useQuery();
  const { update } = useUpdateQuery();
  const { alertToast, successToast } = useToast();
  const createModal = useModal();
  const deleteModal = useModal();

  const searchValue = query.get("search");
  const searchTerm = useDebounce(searchValue);
  const pageNumber = Number(query.get("page")) || 1;

  const [selectedTemplate, setSelectedTemplate] =
    useState<IncorporationBaseTemplate | null>(null);

  const { data, isLoading, isSuccess } = useGetFormTemplateListQuery({
    search: searchTerm,
    page_num: pageNumber,
    page_size: PAGE_SIZE,
  });

  const {
    results: templates = [],
    total_count: totalCount = 0,
    total_pages: totalPages = 1,
    per_page: perPage = PAGE_SIZE,
  } = data || {};

  const [deleteTemplate, { isLoading: isDeleting }] =
    useDeleteFormTemplateMutation();

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    update({ query: "search", value: e.target.value || null });
  };

  const onPageNumChange = (page: number) => {
    update({ query: "page", value: page });
  };

  const onViewTemplate = (template: IncorporationBaseTemplate) => {
    history.push(`/admin/incorporation/templates/${template.uuid}`);
  };

  const onEditTemplate = (template: IncorporationBaseTemplate) => {
    history.push(`/admin/incorporation/templates/${template.uuid}?edit=true`);
  };

  const onDeleteTemplate = (template: IncorporationBaseTemplate) => {
    setSelectedTemplate(template);
    deleteModal.open();
  };

  const confirmDeleteTemplate = async () => {
    if (!selectedTemplate) {
      return;
    }

    try {
      await deleteTemplate({ templateId: selectedTemplate.uuid }).unwrap();
      successToast({ message: "Template deleted successfully" });
      deleteModal.close();
    } catch (err) {
      alertToast({
        message:
          (err as BackendError)?.data?.error?.message ?? "Something went wrong",
      });
    }
  };

  const columnHelper = createColumnHelper<IncorporationBaseTemplate>();

  const columns = [
    columnHelper.accessor("title", {
      header: "Template",
      size: 45,
      cell: ({ row }) => (
        <ToolTip text={row.original.title}>
          <span className="t-truncate t-text-purple">{row.original.title}</span>
        </ToolTip>
      ),
    }),
    columnHelper.accessor("created_by", {
      header: "Made by",
      size: 25,
      cell: ({ row }) => {
        const createdBy = row.original.created_by;

        if (!createdBy) {
          return "—";
        }

        return (
          <span className="t-flex t-items-center t-gap-2">
            <Avatar alt={createdBy} lettersCount={1} size="small" />
            {createdBy}
          </span>
        );
      },
    }),
    columnHelper.accessor("updated_at", {
      header: "Last updated on",
      size: 25,
      cell: ({ row }) => (
        <span className="t-flex t-flex-col">
          {dayjs(row.original.updated_at).format(DISPLAY_DATE)}
        </span>
      ),
    }),
    columnHelper.display({
      id: "actions",
      header: "",
      size: 5,
      cell: ({ row }) => (
        <IncorporationTemplateActions
          template={row.original}
          onEdit={onEditTemplate}
          onDelete={onDeleteTemplate}
        />
      ),
    }),
  ];

  const table = useReactTable({
    data: templates,
    columns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: { minSize: 1 },
  });

  const isEmpty = totalCount === 0;

  return (
    <DashboardLayout
      header={
        <Header
          v2
          title="Templates"
          right={
            <Button
              customType="secondary"
              onClick={createModal.open}
              size="small"
            >
              Add new template
            </Button>
          }
          bottom={
            <div className="t-flex t-justify-between t-items-center t-pb-4 sm:t-flex-wrap sm:t-justify-end sm:t-gap-3">
              <div className="t-w-60 sm:t-w-full">
                <Search
                  block
                  onChange={onSearch}
                  value={searchValue || ""}
                  placeholder="Search"
                />
              </div>
              <PaginationHandler
                page_num={pageNumber}
                per_page={perPage}
                total_count={totalCount}
                total_pages={totalPages}
                onPageNumChange={onPageNumChange}
                isLoading={isLoading}
              />
            </div>
          }
        />
      }
      contentProps={{ className: "t-flex-1 t-min-h-0 t-flex t-flex-col" }}
    >
      <div className="t-flex-1 t-min-h-0 t-overflow-auto">
        <Async.Root
          isLoading={isLoading}
          isEmpty={isEmpty}
          isSuccess={isSuccess}
        >
          <Async.Empty>
            <EmptyScreen text="No templates found" />
          </Async.Empty>
          <Async.Success>
            {isMobile ? (
              <div className="t-flex t-flex-col">
                {table.getRowModel().rows.map((row) => (
                  <MobileIncorporationTemplateRow
                    key={row.id}
                    row={row}
                    onClick={onViewTemplate}
                  />
                ))}
              </div>
            ) : (
              <div className="t-h-full">
                <TableUI
                  table={table}
                  layout="fixed"
                  sticky
                  height="100%"
                  onRowClick={(row) => onViewTemplate(row.original)}
                />
              </div>
            )}
          </Async.Success>
        </Async.Root>
      </div>

      <CreateTemplateModal
        isOpen={createModal.isOpen}
        close={createModal.close}
      />

      {selectedTemplate && (
        <DeleteTemplateModal
          isOpen={deleteModal.isOpen}
          close={deleteModal.close}
          templateName={selectedTemplate.title}
          isLoading={isDeleting}
          onConfirm={confirmDeleteTemplate}
        />
      )}
    </DashboardLayout>
  );
};
